import { Tooltip } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { usePermission } from "@hooks/use-permission";
import { PermissionKey } from "@utils/permissions/permission-matrix";
import { cloneElement } from "react";

interface Props {
  /** The permission the logged in user needs to use the child */
  permission: PermissionKey;

  /**
   * The control to guard, e.g. a button, switch or text field. It has to take a `disabled` prop
   * (not `isDisabled`, in which case use `usePermission` instead) and is disabled, with an
   * explanatory tooltip, when the permission is missing.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention -- name of the prop controls take
  children: React.ReactElement<{ disabled?: boolean }>;
}

/**
 * Disables the child control if the logged in user's roles don't grant the permission.
 * A `disabled` the child already has is kept.
 */
export function RequirePermission({
  permission,
  children,
}: Props): React.JSX.Element {
  const { isAllowed, deniedReason } = usePermission(permission);

  if (isAllowed) {
    return children;
  }

  // The wrapper receives the hover events, because a disabled control doesn't emit them.
  // It is inline-block so it is only as wide as the child, which is where the tooltip anchors.
  return (
    <Tooltip title={deniedReason}>
      <Box component="span" tabIndex={0} sx={{ display: "inline-block" }}>
        {/* eslint-disable-next-line @typescript-eslint/naming-convention -- name of the prop controls take */}
        {cloneElement(children, { disabled: true })}
      </Box>
    </Tooltip>
  );
}
